//#region Imports
import { AfterViewInit, Component, HostListener, OnInit, Renderer2, ViewChild } from '@angular/core';
import { AuthenticationService } from '@app/member-response-portal/service/authentication.service';
import { FooterService } from '@app/member-response-portal/service/footer.service';
import { Helper } from '@rawlings/core';
import { MatSidenav } from '@angular/material/sidenav';
import { MatStepper } from '@angular/material/stepper';
import { Observable, of } from 'rxjs';
import { SharedMemberResponseService } from '@app/member-response-portal/service/shared-member-response.service';
import { SummaryComponent } from '../summary/summary.component';
import { SurveyEmploymentStatusComponent } from '../survey-employment-status/survey-employment-status.component';
import { SurveyOtherBenefitsComponent } from '../survey-other-benefits/survey-other-benefits.component';
import { SurveyOtherHealthInsuranceComponent } from '../survey-other-health-insurance/survey-other-health-insurance.component';
import { SurveySocialSecurityBenefitsComponent } from '../survey-social-security-benefits/survey-social-security-benefits.component';
//#endregion Imports
@Component({
    selector: 'app-survey',
    templateUrl: './survey.component.html',
    styleUrls: ['./survey.component.scss']
})
export class SurveyComponent implements OnInit, AfterViewInit{
    //#region Inputs, Outputs, ViewChilds
    @ViewChild('sidenavStepper', { static: true }) public SidenavStepper: MatSidenav;
    @ViewChild('stepperSurvey') public Stepper: MatStepper;
    @ViewChild(SurveyEmploymentStatusComponent) public EmploymentStatusComponent!: SurveyEmploymentStatusComponent;
    @ViewChild(SurveyOtherBenefitsComponent) public OtherBenefitsComponent!: SurveyOtherBenefitsComponent;
    @ViewChild(SurveyOtherHealthInsuranceComponent) public OtherHealthInsuranceComponent!: SurveyOtherHealthInsuranceComponent;
    @ViewChild(SurveySocialSecurityBenefitsComponent) public SocialSecurityBenefitsComponent!: SurveySocialSecurityBenefitsComponent;
    @ViewChild(SummaryComponent) public SummaryComponent!: SummaryComponent;
    //#endregion Inputs, Outputs, ViewChilds

    //#region Public Variables
    public IsMobile: boolean = false;
    public IsNextButtonMobile: boolean = false;
    public MobileQuery: MediaQueryList;
    //#endregion Public Variables

    //#region Protected Variables
    //#endregion Protected Variables

    //#region Private Variables
    private SurveyResponseId : number = null;
    private ActiveStepIndex : number = 0;
    private MaxSizeSmallScreen : number = 1024;
    private MaxSizeSmallScreenNextButton : number = 768;
    //#endregion Private Variables

    //#region Properties
    public get OtherHealthInsuranceFormIsValid(): boolean {
        return this.OtherHealthInsuranceComponent?.FormIsValid;
    }

    public get SocialSecurityBenefitsFormIsValid(): boolean {
        return this.SocialSecurityBenefitsComponent?.FormIsValid
        && (this.OtherHealthInsuranceFormIsValid);
    }

    public get EmploymentStatusFormIsValid(): boolean{
        return this.EmploymentStatusComponent?.FormIsValid
        && (this.SocialSecurityBenefitsFormIsValid);
    }

    public get OtherBenefitsFormIsValid(): boolean{
        return this.OtherBenefitsComponent?.FormIsValid
        && (this.EmploymentStatusFormIsValid);
    }

    public get SummaryFormIsValid(): boolean{
        return this.SummaryComponent?.FormIsValid
        && (this.OtherBenefitsFormIsValid);
    }

    public get ActiveStep(): number {
        return this.Stepper?.selectedIndex ?? 0;
    }

    //#endregion Properties

    //#region Constructor
    constructor(
        private authenticationService: AuthenticationService,
        private sharedMemberResponseService: SharedMemberResponseService,
        private renderer: Renderer2,
        private dialogService: FooterService
    ) {}

    public ngOnInit(): void {
        this.IsMobile = window.innerWidth < this.MaxSizeSmallScreen;
        this.IsNextButtonMobile = window.innerWidth < this.MaxSizeSmallScreenNextButton;
        this.OnWindowResize();
        this.renderer.listen(window, 'resize', () => this.OnWindowResize());

        this.SurveyResponseId = this.sharedMemberResponseService.GetSurveyResponseId();

        if(Helper.IsEmpty(this.SurveyResponseId)) {
            this.authenticationService.LogoutAndRedirectToLogin();
        }
    }

    public ngAfterViewInit(): void {
        this.SetActiveStepComponent(this.sharedMemberResponseService.GetActiveSurveyStepIndex() ?? this.ActiveStepIndex);
        this.UpdateDynamicFooterHeight();
        window.addEventListener('resize', () => this.UpdateDynamicFooterHeight());
    }
    //#endregion Constructor

    //#region Event Handlers
    @HostListener('window:resize', ['$event'])
    public OnResize(): void {
        this.IsMobile = window.innerWidth < this.MaxSizeSmallScreen;
        this.IsNextButtonMobile = window.innerWidth < this.MaxSizeSmallScreenNextButton;
    }
    //#endregion  Event Handlers

    //#region Public Methods
    public OnClickContactUs(): void {
        this.dialogService.OpenContactUs();
    }

    public OnClickFaq(): void {
        this.dialogService.OpenFaq();
    }

    public OnClickStepForward(index:number) : void{
        if(this.StepForwardAccessIsValid(index)){
            if(this.IsMobile){
                this.SidenavStepper.close();
            }

            this.SubmitPartialSavingActiveForm().subscribe((success: boolean) =>{
                if (success){
                    this.SetActiveStepComponent(index);
                }
            });
        }
    }

    public OnClickStepBackward(index:number) : void{
        this.SubmitPartialSavingActiveForm().subscribe((success: boolean) =>{
            if (success){
                this.SetActiveStepComponent(index);
            }
        });
    }

    public OnClickStepSubmit(index:number) : void{
        if(this.FormStepIsValid(index)){
            this.SubmitFormStep(index).subscribe((success: boolean) => {
                if (success){
                    this.SetActiveStepComponent(index + 1);
                }
            });
        }
    }

    public OnClickWhoWeAre(): void {
        this.dialogService.OpenWhoWeAre();
    }
    //#endregion Public Methods
    //#region Private Methods
    private SetActiveStepComponent(index:number) : void {
        this.Stepper.selectedIndex = index;
        this.ActiveStepIndex = index;
        this.sharedMemberResponseService.SetActiveSurveyStepIndex(index);

        if(this.OtherHealthInsuranceComponent){
            this.OtherHealthInsuranceComponent.ActiveStep = index === this.OtherHealthInsuranceComponent.StepIndex;
        }

        if(this.SocialSecurityBenefitsComponent){
            this.SocialSecurityBenefitsComponent.ActiveStep = index === this.SocialSecurityBenefitsComponent.StepIndex;
        }

        if(this.EmploymentStatusComponent){
            this.EmploymentStatusComponent.ActiveStep = index === this.EmploymentStatusComponent.StepIndex;
        }

        if(this.OtherBenefitsComponent){
            this.OtherBenefitsComponent.ActiveStep = index === this.OtherBenefitsComponent.StepIndex;
        }

        if(this.SummaryComponent){
            this.SummaryComponent.ActiveStep = index === this.SummaryComponent.StepIndex;
            if (this.SummaryComponent.ActiveStep){
                this.SummaryComponent.LoadData();
            }
        }
    }

    private FormStepIsValid(index:number) : boolean {
        switch(index){
            case this.OtherHealthInsuranceComponent.StepIndex:
            return this.OtherHealthInsuranceFormIsValid;
            case this.SocialSecurityBenefitsComponent.StepIndex:
            return this.SocialSecurityBenefitsFormIsValid;
            case this.EmploymentStatusComponent.StepIndex:
            return this.EmploymentStatusFormIsValid;
            case this.OtherBenefitsComponent.StepIndex:
            return this.OtherBenefitsFormIsValid;
            case this.SummaryComponent.StepIndex:
            return this.SummaryFormIsValid;
            default:
            return false;
        }
    }

    private StepForwardAccessIsValid(index:number) : boolean {
        switch(index){
            case this.OtherHealthInsuranceComponent.StepIndex:
            return true;
            case this.SocialSecurityBenefitsComponent.StepIndex:
            return this.OtherHealthInsuranceFormIsValid;
            case this.EmploymentStatusComponent.StepIndex:
            return this.SocialSecurityBenefitsFormIsValid;
            case this.OtherBenefitsComponent.StepIndex:
            return this.EmploymentStatusFormIsValid;
            case this.SummaryComponent.StepIndex:
            return this.OtherBenefitsFormIsValid;
            default:
            return false;
        }
    }

    private SubmitFormStep(index:number) : Observable<boolean> {
        switch(index){
            case this.OtherHealthInsuranceComponent.StepIndex:
                return this.OtherHealthInsuranceComponent.OnSubmit();
            case this.SocialSecurityBenefitsComponent.StepIndex:
                return this.SocialSecurityBenefitsComponent.OnSubmit();
            case this.EmploymentStatusComponent.StepIndex:
                return this.EmploymentStatusComponent.OnSubmit()
            case this.OtherBenefitsComponent.StepIndex:
                return this.OtherBenefitsComponent.OnSubmit();
            case this.SummaryComponent.StepIndex:
                return this.SummaryComponent.OnSubmit();
            default:
                return of(false);
        }
    }

    private SavePartialFormStep(index:number) : Observable<boolean> {
        switch(index){
            case this.OtherHealthInsuranceComponent.StepIndex:
            if(this.OtherHealthInsuranceComponent.Form.dirty){
                return this.OtherHealthInsuranceComponent.SaveForm();
            }
            return of(true);

            case this.SocialSecurityBenefitsComponent.StepIndex:
            if(this.SocialSecurityBenefitsComponent.Form.dirty){
                return this.SocialSecurityBenefitsComponent.SaveForm();
            }
            return of(true);
            case this.EmploymentStatusComponent.StepIndex:
            if(this.EmploymentStatusComponent.Form.dirty){
                return this.EmploymentStatusComponent.SaveForm();
            }
            return of(true);

            case this.OtherBenefitsComponent.StepIndex:
            if(this.OtherBenefitsComponent.Form.dirty){
                return this.OtherBenefitsComponent.SaveForm();
            }
            return of(true);
            case this.SummaryComponent.StepIndex:
            if(this.SummaryComponent.ContactForm.dirty){
                return this.SummaryComponent.OnSubmit(false);
            }
            return of(true);
            default:
            return of(false);
        }
    }

    private SubmitPartialSavingActiveForm() : Observable<boolean> {
        if(this.ActiveStepIndex === this.SummaryComponent.StepIndex){
            if(this.SummaryComponent.ContactForm?.dirty){
                return this.SavePartialFormStep(this.ActiveStepIndex);
            }
            return of(true);
        } else{
            return this.SavePartialFormStep(this.ActiveStepIndex);
        }

    }

    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    private OnWindowResize(): void {
        this.IsMobile = window.innerWidth < this.MaxSizeSmallScreen;
        if (this.IsMobile) {
            this.SidenavStepper?.close();
        } else{
            this.SidenavStepper?.open();
        }
    }

    private UpdateDynamicFooterHeight(): void {
        const footerElement: HTMLElement = document.getElementById('footer');
        const headerElement: HTMLElement = document.getElementById('mrp-header');

        if (footerElement) {
            const footerHeight: number = footerElement.offsetHeight;
            document.documentElement.style.setProperty('--footer-height', `${footerHeight}px`);
        }
        if (headerElement) {
            const headerHeight: number = headerElement.offsetHeight;
            document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
        }
    }
    //#region Private Methods
}
